import { BetaFormData } from "../types/BetaFormData";
import { logError } from "../lib/errorLogger";

export interface BetaSignupResult {
  success: boolean;
  message: string;
  documentId: string;
}

export class BetaSignupError extends Error {
  constructor(public code: string, message: string) {
    super(message);
    this.name = "BetaSignupError";
  }
}

export async function submitBetaSignup(
  formData: BetaFormData
): Promise<BetaSignupResult> {
  try {
    const response = await fetch("/api/betaSignup", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const errorMap: Record<string, string> = {
        "already-exists": "This email is already registered for the beta.",
        "invalid-argument": data.message || "Invalid form data. Please check your inputs.",
        "resource-exhausted": "Service temporarily unavailable. Please try again later.",
        "internal": "Oops! Something went wrong on our end. Please try again in a few moments.",
      };

      const code = data.code || `http-${response.status}`;
      const message =
        errorMap[data.code] ||
        data.message ||
        "Oops! Something went wrong on our end. Please try again in a few moments.";

      const error = new BetaSignupError(code, message);

      logError(error, {
        component: "BetaTesterFormService",
        action: "submitBetaSignup",
        httpStatus: response.status,
        responseCode: data.code,
      });

      throw error;
    }

    return data as BetaSignupResult;
  } catch (error: any) {
    if (error instanceof BetaSignupError) {
      throw error;
    }

    logError(error, {
      component: "BetaTesterFormService",
      action: "submitBetaSignup",
    });

    if (error.message?.includes("fetch") || error.message?.includes("network")) {
      throw new BetaSignupError(
        "network-error",
        "Network error. Please check your connection."
      );
    }

    throw new BetaSignupError(
      "unknown",
      "An unexpected error occurred. Please try again."
    );
  }
}